import { useForm } from 'react-hook-form'
import { InputText } from '../../common/LetterForms/InputText'
import { InputDropdownGroup } from '../../common/LetterForms/InputDropdownGroup'
import { InputRadioGroup } from '../../common/LetterForms/InputRadioGroup'
import Button from '@mui/material/Button'
import { Paper, TextField } from '@mui/material'
import { InputFilePicker } from '../../common/LetterForms/InputFilePicker'
import { getCondoListApi, sendLetterApi } from './api'
import { useEffect, useMemo, useState } from 'react'
import { InputAutoComplete } from '../../common/LetterForms/InputAutoComplete'
import { useNavigate } from 'react-router'
import { TemplateInput } from './components/TemplateInput'
import { fetchTemplatesByType } from '../LetterTemplatesList/api'
import Box from '@mui/material/Box'
import {
  BODY_TYPE,
  colorOptions,
  countriesList,
  letterTypeOptions,
  postageTypeOptions,
  templateTypes,
} from './data/constants'
import LoadingScreen from '../../common/LoadingScreen/components/LoadingScreen'
import { Backdrop, Dialog, Typography, styled } from '@material-ui/core'
import moment from 'moment'

const formStyle = {
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  padding: '30px',
}

const Title = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  margin: '1rem',
  fontSize: '2rem',
  color: theme.palette.primary.main,
  fontWeight: 400,
}))

const Preview = styled(TextField)(() => ({
  '& .MuiInputBase-root': {
    border: '1px solid rgba(0, 0, 0, .26)',
    height: 'calc(100vh - 138px)',
    marginTop: '4px',
    alignItems: 'flex-start',
    overflowY: 'auto',
  },
  '& fieldset.MuiOutlinedInput-notchedOutline': {
    border: '0',
  },
}))

const errorToSentence = {
  address:
    'Le champ "Adresse" est requis & doit comporter 38 caractères maximum.',
  body: 'Le champ "Contenu du courrier" est requis.',
  city: 'Le champ "Ville" est requis & doit comporter 38 caractères maximum.',
  color: 'Le champ "Couleur impression" est requis.',
  condominium: 'Le champ "Copropriété" est requis.',
  description: 'Le champ "Objet du courrier" est requis.',
  ending: 'Le champ "Formule de remerciement" est requis.',
  fullName:
    'Le champ "Nom/Prénom du destinataire" est requis & doit comporter 38 caractères maximum.',
  introduction: 'Le champ "Formule d\'introduction" est requis.',
  postageType: 'Le champ "Type d\'envoi" est requis.',
  postalCode:
    'Le champ "Code postal" est requis et doit comprendre 5 chiffres.',
  referentName:
    'Le champ "Nom du référent Manda (Ex-Hello Syndic)" est requis.',
  signature: 'Le champ "Signature" est requis.',
  email: 'Le champ "Email" est requis.',
  firstName:
    'Le champ "Prénom du destinataire" est requis & doit comporter 38 caractères maximum.',
  lastName:
    'Le champ "Nom du destinataire" est requis & doit comporter 38 caractères maximum.',
}

const clean = (value) => {
  return value
    .replace(/<\/p>/g, '\n')
    .replace(/<\/?[^>]+(>|$)/g, '')
    .replace(/&.+;/g, '')
}

const preview = (values) => {
  return [
    `Objet : ${values.description ?? ''}`,
    `Destinataire : ${
      values.letterType !== 'paper'
        ? `${values.lastName ?? ''} ${values.firstName ?? ''}`
        : values.fullName ?? ''
    }`,
    `Propriété : ${values.condominium?.name ?? ''}`,
    `Réferent·e : ${values.referentName ?? ''}`,
    '',
    `À Paris, le ${moment().format('D MMMM YYYY')}`,
    '',
    clean(values.introduction),
    '',
    clean(values.body),
    '',
    clean(values.ending),
    '',
    clean(values.signature),
  ].join('\n')
}

export const LetterSendingForm = () => {
  const [condoList, setCondoList] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [selectTemplatesOptions, setSelectTemplatesOptions] = useState(null)
  const navigate = useNavigate()

  const capitalize = (word) => word.charAt(0).toUpperCase() + word.slice(1)

  useEffect(() => {
    getCondoListApi().then((res) => {
      const condoListTransformed = res.map((item) => ({
        name: item.name,
        id: item.id,
      }))
      setCondoList(condoListTransformed)
    })

    Promise.all(
      templateTypes.map(async (templateType) => {
        const res = await fetchTemplatesByType(templateType)
        const templateListTransformed = res.map((template) => ({
          label: template.name,
          value: template.body,
        }))
        return { name: templateType, data: templateListTransformed }
      })
    ).then((templateOptions) => {
      setSelectTemplatesOptions(templateOptions)
    })
    setIsLoading(false)
  }, [])

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
    getValues,
    clearErrors,
  } = useForm({
    defaultValues: {
      description: '',
      referentName: '',
      postageType: '',
      color: '',
      fullName: '',
      address: '',
      additionalAddress: '',
      country: 'France',
      postalCode: '',
      city: '',
      files: '',
      condominium: null,
      introduction: '',
      signature: '',
      ending: '',
      body: '',
      letterType: 'paper',
      email: '',
      introductionTemplate: '',
      signatureTemplate: '',
      signatureContent: '',
      endingTemplate: '',
      bodyTemplate: '',
    },
    reValidateMode: 'onSubmit',
  })

  const isElectronic = useMemo(() => {
    return watch('letterType') === 'electronic'
  }, [watch('letterType')])

  const onSubmit = async (data) => {
    setIsLoading(true)
    const formData = new FormData()
    const filesArray = [...data.files]
    filesArray.forEach((file) => {
      formData.append('files', file)
    })
    formData.append('description', data.description)
    formData.append('referentName', data.referentName)
    formData.append('signature', data.signature)
    formData.append('ending', data.ending)
    formData.append('introduction', data.introduction)
    formData.append('body', data.body)
    formData.append('color', data.color)
    formData.append('additionalAddress', data.additionalAddress)
    formData.append('address', data.address)
    formData.append('city', data.city)
    formData.append('country', data.country)
    if (data.letterType === 'electronic') {
      formData.append('firstName', data.firstName)
      formData.append('lastName', data.lastName)
    } else {
      formData.append('fullName', data.fullName)
    }

    formData.append('postalCode', data.postalCode)
    formData.append('condominiumId', data.condominium.id)
    formData.append('condominiumName', data.condominium.name)
    formData.append('letterType', data.letterType)
    formData.append('email', data.email)
    formData.append('postageType', data.postageType)

    sendLetterApi(formData).then((res) => {
      setIsLoading(false)
      if (res.status === 201) {
        navigate(`/send-letter/success/${true}`)
      } else {
        navigate(`/send-letter/success/${false}`)
      }
    })
  }

  const formValues = watch()

  const customTranslation = (word) => {
    switch (word) {
      case 'body':
        return 'contenu du courrier'
      case 'introduction':
        return "formule d'introduction"
      case 'ending':
        return 'formule de remerciement'
      default:
        return word
    }
  }

  const errorKeys = Object.keys(errors)

  return (
    <Box
      sx={{
        width: '100%',
        bgcolor: 'background.default',
        display: 'flex',
        alignItems: 'flex-start',
        height: '100%',
        position: 'relative',
      }}
    >
      <Paper
        elevation={2}
        sx={{
          height: '100%',
          width: '66%',
        }}
      >
        <Title>Envoyer un nouveau courrier</Title>
        {isLoading ? (
          <LoadingScreen />
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} style={formStyle}>
            <Typography component="p" style={{ margin: '0 0 1rem' }}>
              Les champs comportant un astérisque (*) sont requis.
            </Typography>

            <InputDropdownGroup
              name={'letterType'}
              control={control}
              label="Type de courrier"
              required
              options={letterTypeOptions}
            />
            {isElectronic ? (
              <>
                <InputText
                  name={'lastName'}
                  control={control}
                  label={'Nom du destinataire *'}
                  required
                  maxLength={38}
                />
                <InputText
                  name={'firstName'}
                  control={control}
                  label={'Prénom du destinataire *'}
                  required
                  maxLength={38}
                />
              </>
            ) : (
              <InputText
                name={'fullName'}
                control={control}
                label={'Nom/Prénom du destinataire *'}
                required
                maxLength={38}
              />
            )}
            <InputText
              name={'description'}
              control={control}
              label={'Objet du courrier *'}
              required
            />
            <InputText
              name={'referentName'}
              control={control}
              label={'Nom du référent Manda (Ex-Hello Syndic) *'}
              required
            />
            {condoList && (
              <InputAutoComplete
                name={'condominium'}
                control={control}
                label={'Copropriété *'}
                options={condoList}
                required
              />
            )}
            {isElectronic ? (
              <InputText
                name={'email'}
                control={control}
                label={'Email *'}
                required={isElectronic}
                isEmail
              />
            ) : (
              <>
                <InputText
                  name={'address'}
                  control={control}
                  label={'Adresse *'}
                  required={!isElectronic}
                  maxLength={38}
                />
                <InputText
                  name={'additionalAddress'}
                  control={control}
                  label={"Complément d'adresse"}
                  maxLength={38}
                />
                <InputDropdownGroup
                  name={'country'}
                  control={control}
                  label={'Pays'}
                  required={!isElectronic}
                  options={countriesList}
                  maxLength={38}
                />
                <InputText
                  name={'postalCode'}
                  control={control}
                  label={'Code postal *'}
                  required={!isElectronic}
                  inputType="number"
                  maxLength={5}
                />
                <InputText
                  name={'city'}
                  control={control}
                  label={'Ville *'}
                  required={!isElectronic}
                  maxLength={38}
                />
                <InputDropdownGroup
                  name={'color'}
                  control={control}
                  label={'Couleur impression *'}
                  required={!isElectronic}
                  options={colorOptions}
                />
                <InputRadioGroup
                  name={'postageType'}
                  control={control}
                  label={"Type d'envoi *"}
                  required={!isElectronic}
                  options={postageTypeOptions}
                />
              </>
            )}
            <InputFilePicker name="files" control={control} />

            {selectTemplatesOptions &&
              selectTemplatesOptions.length > 0 &&
              selectTemplatesOptions.map((option, index) => (
                <TemplateInput
                  key={index}
                  control={control}
                  options={option.data}
                  selectFieldName={`${option.name}Template`}
                  selectLabel={"Optionnel - Choix d'un template"}
                  editUrl={`letters_templates/${option.name}`}
                  ctaTitle={'Créer ou éditer un template'}
                  editorFieldName={option.name}
                  editorLabel={capitalize(
                    customTranslation(option.name) + ' *'
                  )}
                  isSmall={option.name === BODY_TYPE ? false : true}
                  required
                  setValue={setValue}
                />
              ))}

            <Backdrop open={!!errorKeys.length} onClick={() => clearErrors()}>
              <Dialog open={!!errorKeys.length}>
                <Typography component="p" style={{ padding: '1rem 1rem 0' }}>
                  Retrouvez ci-dessous la liste des erreurs :
                </Typography>
                <Box component="ul" sx={{ padding: '1rem', margin: '0 1rem' }}>
                  {errorKeys.map((key, index) => (
                    <Typography key={index} component="li">
                      {errorToSentence[key]}
                    </Typography>
                  ))}
                </Box>
              </Dialog>
            </Backdrop>

            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{ width: '200px' }}
            >
              Envoyer
            </Button>
          </form>
        )}
      </Paper>
      <Paper
        elevation={2}
        sx={{
          width: '34%',
          position: 'sticky',
          top: 48,
          height: 'calc(100vh - 48px)',
        }}
      >
        <Box style={formStyle}>
          <Typography>Aperçu</Typography>
          <Preview
            multiline
            disabled
            variant="outlined"
            value={preview(formValues)}
          />
        </Box>
      </Paper>
    </Box>
  )
}
